import React, { useState, useEffect, useMemo } from 'react';
import './TradFiTrading.css';
import BybitCredentials from './BybitCredentials';

const TradFiTrading = ({ allTrades = [], addTrade, updateTrade, closeTrade }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [accountInfo, setAccountInfo] = useState(null);
  const [marketData, setMarketData] = useState({});
  const [selectedInstrument, setSelectedInstrument] = useState('EURUSD');
  const [orderData, setOrderData] = useState({
    volume: 0.01,
    type: 'BUY',
    stopLoss: 0,
    takeProfit: 0,
    comment: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('Disconnected');

  // TradFi instrument categories
  const instruments = {
    FOREX: [
      { symbol: 'EURUSD', name: 'Euro/US Dollar', category: 'Major' },
      { symbol: 'GBPUSD', name: 'British Pound/US Dollar', category: 'Major' },
      { symbol: 'USDJPY', name: 'US Dollar/Japanese Yen', category: 'Major' },
      { symbol: 'USDCHF', name: 'US Dollar/Swiss Franc', category: 'Major' },
      { symbol: 'AUDUSD', name: 'Australian Dollar/US Dollar', category: 'Major' },
      { symbol: 'USDCAD', name: 'US Dollar/Canadian Dollar', category: 'Major' },
      { symbol: 'NZDUSD', name: 'New Zealand Dollar/US Dollar', category: 'Major' },
      { symbol: 'EURGBP', name: 'Euro/British Pound', category: 'Cross' },
      { symbol: 'EURJPY', name: 'Euro/Japanese Yen', category: 'Cross' },
      { symbol: 'GBPJPY', name: 'British Pound/Japanese Yen', category: 'Cross' }
    ],
    COMMODITIES: [
      { symbol: 'XAUUSD', name: 'Gold/US Dollar', category: 'Precious Metals' },
      { symbol: 'XAGUSD', name: 'Silver/US Dollar', category: 'Precious Metals' },
      { symbol: 'USOIL', name: 'Crude Oil WTI', category: 'Energy' },
      { symbol: 'UKOIL', name: 'Crude Oil Brent', category: 'Energy' },
      { symbol: 'NATGAS', name: 'Natural Gas', category: 'Energy' }
    ],
    INDICES: [
      { symbol: 'SPX500', name: 'S&P 500', category: 'US Indices' },
      { symbol: 'NAS100', name: 'NASDAQ 100', category: 'US Indices' },
      { symbol: 'UK100', name: 'FTSE 100', category: 'UK Indices' },
      { symbol: 'GER30', name: 'DAX 30', category: 'German Indices' },
      { symbol: 'FRA40', name: 'CAC 40', category: 'French Indices' },
      { symbol: 'JPN225', name: 'Nikkei 225', category: 'Japanese Indices' }
    ],
    CRYPTO: [
      { symbol: 'BTCUSDT', name: 'Bitcoin/USDT', category: 'Major Crypto' },
      { symbol: 'ETHUSDT', name: 'Ethereum/USDT', category: 'Major Crypto' },
      { symbol: 'LTCUSDT', name: 'Litecoin/USDT', category: 'Major Crypto' },
      { symbol: 'XRPUSDT', name: 'Ripple/USDT', category: 'Major Crypto' },
      { symbol: 'ADAUSDT', name: 'Cardano/USDT', category: 'Major Crypto' },
      { symbol: 'SOLUSDT', name: 'Solana/USDT', category: 'Major Crypto' },
      { symbol: 'LINKUSDT', name: 'Chainlink/USDT', category: 'Major Crypto' }
    ]
  };

  // Filter TradFi trades from all trades
  const tradFiTrades = allTrades.filter(trade => 
    trade.type === 'TRADFI' || 
    Object.values(instruments).flat().some(inst => inst.symbol === trade.pair)
  );

  // Connect to MT5/TradFi platform
  const connectToTradFi = async () => {
    setIsLoading(true);
    setConnectionStatus('Connecting...');
    
    try {
      // Connect to backend TradFi API
      const response = await fetch('/api/tradfi/connect', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          isDemo: false
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        // Use real account info from API
        const accountInfo = result.accountInfo;
        setAccountInfo({
          accountNumber: accountInfo.accountId || '12345678',
          balance: accountInfo.totalWalletBalance || 10000.00,
          equity: accountInfo.totalEquity || 10000.00,
          margin: accountInfo.totalMargin || 0.00,
          freeMargin: accountInfo.availableBalance || 10000.00,
          marginLevel: accountInfo.marginLevel || 0,
          currency: 'USD',
          leverage: 100,
          server: 'Bybit-Live'
        });
        
        setIsConnected(true);
        setConnectionStatus('Connected');
        
        // Start fetching real market data
        startMarketDataFeed();
      } else {
        throw new Error(result.error || 'Connection failed');
      }
      
    } catch (error) {
      console.error('Failed to connect to TradFi:', error);
      setConnectionStatus('Connection Failed — check API keys');
    } finally {
      setIsLoading(false);
    }
  };

  // Disconnect from TradFi platform
  const disconnectFromTradFi = () => {
    setIsConnected(false);
    setAccountInfo(null);
    setMarketData({});
    setConnectionStatus('Disconnected');
  };

  // Start real-time market data feed
  const startMarketDataFeed = () => {
    const updatePrices = async () => {
      try {
        // Try to get real market data from API
        const response = await fetch('/api/tradfi/market-data');
        const result = await response.json();
        
        if (result.success && result.marketData) {
          // Use real market data
          setMarketData(result.marketData);
        } else {
          // Fallback to simulated data
          const newMarketData = {};
          
          Object.values(instruments).flat().forEach(instrument => {
            const basePrice = getBasePriceForInstrument(instrument.symbol);
            const spread = getSpreadForInstrument(instrument.symbol);
            const volatility = getVolatilityForInstrument(instrument.symbol);
            
            // Simulate price movement
            const change = (Math.random() - 0.5) * volatility;
            const newPrice = basePrice + change;
            
            newMarketData[instrument.symbol] = {
              bid: newPrice - spread / 2,
              ask: newPrice + spread / 2,
              last: newPrice,
              change: change,
              changePercent: (change / basePrice) * 100,
              timestamp: new Date().toISOString()
            };
          });
          
          setMarketData(newMarketData);
        }
      } catch (error) {
        console.error('Failed to fetch market data:', error);
        
        // Fallback to simulated data
        const newMarketData = {};
        
        Object.values(instruments).flat().forEach(instrument => {
          const basePrice = getBasePriceForInstrument(instrument.symbol);
          const spread = getSpreadForInstrument(instrument.symbol);
          const volatility = getVolatilityForInstrument(instrument.symbol);
          
          // Simulate price movement
          const change = (Math.random() - 0.5) * volatility;
          const newPrice = basePrice + change;
          
          newMarketData[instrument.symbol] = {
            bid: newPrice - spread / 2,
            ask: newPrice + spread / 2,
            last: newPrice,
            change: change,
            changePercent: (change / basePrice) * 100,
            timestamp: new Date().toISOString()
          };
        });
        
        setMarketData(newMarketData);
      }
    };

    // Update prices every 2 seconds
    const interval = setInterval(updatePrices, 2000);
    
    // Initial update
    updatePrices();
    
    return () => clearInterval(interval);
  };

  // Get base price for different instruments
  const getBasePriceForInstrument = (symbol) => {
    const basePrices = {
      'EURUSD': 1.0850, 'GBPUSD': 1.2650, 'USDJPY': 149.50, 'USDCHF': 0.8750,
      'AUDUSD': 0.6550, 'USDCAD': 1.3650, 'NZDUSD': 0.6050, 'EURGBP': 0.8580,
      'EURJPY': 162.20, 'GBPJPY': 189.20,
      'XAUUSD': 2020.00, 'XAGUSD': 24.50, 'USOIL': 75.50, 'UKOIL': 80.20, 'NATGAS': 2.85,
      'SPX500': 4500.00, 'NAS100': 15000.00, 'UK100': 7500.00, 'GER30': 16000.00,
      'FRA40': 7200.00, 'JPN225': 33000.00,
      'BTCUSDT': 45000.00, 'ETHUSDT': 2800.00, 'LTCUSDT': 75.00, 'XRPUSDT': 0.65,
      'ADAUSDT': 0.45, 'SOLUSDT': 100.00, 'LINKUSDT': 15.50
    };
    return basePrices[symbol] || 1.0000;
  };

  // Get spread for different instruments
  const getSpreadForInstrument = (symbol) => {
    const spreads = {
      'EURUSD': 0.0001, 'GBPUSD': 0.0002, 'USDJPY': 0.01, 'USDCHF': 0.0002,
      'AUDUSD': 0.0002, 'USDCAD': 0.0002, 'NZDUSD': 0.0003, 'EURGBP': 0.0002,
      'EURJPY': 0.02, 'GBPJPY': 0.03,
      'XAUUSD': 0.50, 'XAGUSD': 0.05, 'USOIL': 0.05, 'UKOIL': 0.05, 'NATGAS': 0.01,
      'SPX500': 0.5, 'NAS100': 1.0, 'UK100': 1.0, 'GER30': 1.0,
      'FRA40': 1.0, 'JPN225': 5.0,
      'BTCUSDT': 5.0, 'ETHUSDT': 2.0, 'LTCUSDT': 0.5, 'XRPUSDT': 0.001,
      'ADAUSDT': 0.01, 'SOLUSDT': 2.0, 'LINKUSDT': 0.5
    };
    return spreads[symbol] || 0.0001;
  };

  // Get volatility for different instruments
  const getVolatilityForInstrument = (symbol) => {
    const volatilities = {
      'EURUSD': 0.0001, 'GBPUSD': 0.0002, 'USDJPY': 0.01, 'USDCHF': 0.0002,
      'AUDUSD': 0.0002, 'USDCAD': 0.0002, 'NZDUSD': 0.0003, 'EURGBP': 0.0002,
      'EURJPY': 0.02, 'GBPJPY': 0.03,
      'XAUUSD': 2.0, 'XAGUSD': 0.2, 'USOIL': 0.5, 'UKOIL': 0.5, 'NATGAS': 0.1,
      'SPX500': 5.0, 'NAS100': 10.0, 'UK100': 10.0, 'GER30': 10.0,
      'FRA40': 10.0, 'JPN225': 50.0,
      'BTCUSDT': 100.0, 'ETHUSDT': 50.0, 'LTCUSDT': 5.0, 'XRPUSDT': 0.01,
      'ADAUSDT': 0.01, 'SOLUSDT': 5.0, 'LINKUSDT': 0.5
    };
    return volatilities[symbol] || 0.0001;
  };

  // Place a TradFi order
  const placeTradFiOrder = async () => {
    if (!isConnected) {
      alert('Please connect to TradFi platform first');
      return;
    }

    setIsLoading(true);
    
    try {
      const instrument = Object.values(instruments).flat().find(inst => inst.symbol === selectedInstrument);
      const currentPrice = marketData[selectedInstrument];
      
      if (!currentPrice) {
        alert('No market data available for selected instrument');
        return;
      }

      const trade = {
        id: Date.now().toString(),
        pair: selectedInstrument,
        action: orderData.type,
        amount: orderData.volume,
        leverage: accountInfo?.leverage || 100,
        entryPrice: orderData.type === 'BUY' ? currentPrice.ask : currentPrice.bid,
        currentPrice: orderData.type === 'BUY' ? currentPrice.ask : currentPrice.bid,
        timestamp: new Date().toISOString(),
        type: 'TRADFI',
        status: 'ACTIVE',
        profitLoss: 0,
        profitPercent: 0,
        tradFiData: {
          instrument: instrument,
          volume: orderData.volume,
          stopLoss: orderData.stopLoss,
          takeProfit: orderData.takeProfit,
          comment: orderData.comment,
          spread: currentPrice.ask - currentPrice.bid
        }
      };

      addTrade(trade);
      
      // Reset form
      setOrderData({
        volume: 0.01,
        type: 'BUY',
        stopLoss: 0,
        takeProfit: 0,
        comment: ''
      });
      
      alert(`TradFi order placed successfully!`);
      
    } catch (error) {
      console.error('Failed to place TradFi order:', error);
      alert('Failed to place order. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Close a TradFi position
  const closeTradFiPosition = (tradeId) => {
    closeTrade(tradeId);
  };

  // Auto-connect on component mount
  useEffect(() => {
    if (!isConnected) {
      connectToTradFi();
    }
  }, []);

  return (
    <div className="tradfi-trading">
      <div className="tradfi-header">
        <h3>📈 TradFi Trading Platform</h3>
        <div className="header-actions">
          <BybitCredentials onCredentialsSet={(creds) => {
            console.log('Credentials updated:', creds);
            // Reload connection with new credentials
            if (isConnected) {
              disconnectFromTradFi();
              setTimeout(() => connectToTradFi(), 1000);
            }
          }} />
          <div className="connection-status">
          <div className={`status-indicator ${isConnected ? 'connected' : 'disconnected'}`}>
            <div className="status-dot"></div>
            <span>{connectionStatus}</span>
          </div>
          {isConnected ? (
            <button className="disconnect-btn" onClick={disconnectFromTradFi}>
              Disconnect
            </button>
          ) : (
            <button className="connect-btn" onClick={connectToTradFi} disabled={isLoading}>
              {isLoading ? 'Connecting...' : 'Connect'}
            </button>
          )}
          </div>
        </div>
      </div>

      {isConnected && accountInfo && (
        <div className="tradfi-content">
          {/* Account Information */}
          <div className="account-info-section">
            <h4>Account Information</h4>
            <div className="account-details">
              <div className="account-item">
                <span className="label">Account:</span>
                <span className="value">{accountInfo.accountNumber}</span>
              </div>
              <div className="account-item">
                <span className="label">Balance:</span>
                <span className="value">${accountInfo.balance.toFixed(2)}</span>
              </div>
              <div className="account-item">
                <span className="label">Equity:</span>
                <span className="value">${accountInfo.equity.toFixed(2)}</span>
              </div>
              <div className="account-item">
                <span className="label">Free Margin:</span>
                <span className="value">${accountInfo.freeMargin.toFixed(2)}</span>
              </div>
              <div className="account-item">
                <span className="label">Leverage:</span>
                <span className="value">1:{accountInfo.leverage}</span>
              </div>
              <div className="account-item">
                <span className="label">Server:</span>
                <span className="value">{accountInfo.server}</span>
              </div>
            </div>
          </div>

          {/* Market Data */}
          <div className="market-data-section">
            <h4>Market Data</h4>
            <div className="instruments-tabs">
              {Object.entries(instruments).map(([category, insts]) => (
                <div key={category} className="instrument-category">
                  <h5>{category}</h5>
                  <div className="instruments-grid">
                    {insts.map(instrument => {
                      const data = marketData[instrument.symbol];
                      return (
                        <div key={instrument.symbol} className="instrument-card">
                          <div className="instrument-header">
                            <span className="symbol">{instrument.symbol}</span>
                            <span className="category">{instrument.category}</span>
                          </div>
                          <div className="instrument-name">{instrument.name}</div>
                          {data && (
                            <div className="instrument-prices">
                              <div className="price-row">
                                <span className="label">Bid:</span>
                                <span className="price">{data.bid.toFixed(5)}</span>
                              </div>
                              <div className="price-row">
                                <span className="label">Ask:</span>
                                <span className="price">{data.ask.toFixed(5)}</span>
                              </div>
                              <div className="price-row">
                                <span className="label">Change:</span>
                                <span className={`change ${data.change >= 0 ? 'positive' : 'negative'}`}>
                                  {data.change >= 0 ? '+' : ''}{data.change.toFixed(5)} ({data.changePercent.toFixed(2)}%)
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Trading Panel */}
          <div className="trading-panel-section">
            <h4>Place Order</h4>
            <div className="trading-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Instrument:</label>
                  <select 
                    value={selectedInstrument} 
                    onChange={(e) => setSelectedInstrument(e.target.value)}
                  >
                    {Object.values(instruments).flat().map(inst => (
                      <option key={inst.symbol} value={inst.symbol}>
                        {inst.symbol} - {inst.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Order Type:</label>
                  <select 
                    value={orderData.type} 
                    onChange={(e) => setOrderData(prev => ({ ...prev, type: e.target.value }))}
                  >
                    <option value="BUY">Buy</option>
                    <option value="SELL">Sell</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Volume:</label>
                  <input 
                    type="number" 
                    value={orderData.volume} 
                    onChange={(e) => setOrderData(prev => ({ ...prev, volume: parseFloat(e.target.value) }))}
                    min="0.01"
                    step="0.01"
                    placeholder="0.01"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Stop Loss:</label>
                  <input 
                    type="number" 
                    value={orderData.stopLoss} 
                    onChange={(e) => setOrderData(prev => ({ ...prev, stopLoss: parseFloat(e.target.value) }))}
                    step="0.00001"
                    placeholder="0.00000"
                  />
                </div>
                <div className="form-group">
                  <label>Take Profit:</label>
                  <input 
                    type="number" 
                    value={orderData.takeProfit} 
                    onChange={(e) => setOrderData(prev => ({ ...prev, takeProfit: parseFloat(e.target.value) }))}
                    step="0.00001"
                    placeholder="0.00000"
                  />
                </div>
                <div className="form-group">
                  <label>Comment:</label>
                  <input 
                    type="text" 
                    value={orderData.comment} 
                    onChange={(e) => setOrderData(prev => ({ ...prev, comment: e.target.value }))}
                    placeholder="Optional comment"
                  />
                </div>
              </div>
              <button 
                className="place-order-btn"
                onClick={placeTradFiOrder}
                disabled={isLoading}
              >
                {isLoading ? 'Placing Order...' : 'Place Order'}
              </button>
            </div>
          </div>

          {/* Active Positions */}
          <div className="positions-section">
            <h4>Active Positions ({tradFiTrades.filter(t => t.status === 'ACTIVE').length})</h4>
            {tradFiTrades.filter(t => t.status === 'ACTIVE').length === 0 ? (
              <div className="no-positions">
                <div className="no-positions-icon">📊</div>
                <h5>No Active Positions</h5>
                <p>You don't have any active TradFi positions. Place an order to get started.</p>
              </div>
            ) : (
              <div className="positions-list">
                {tradFiTrades.filter(t => t.status === 'ACTIVE').map(trade => {
                  const currentData = marketData[trade.pair];
                  const profitLoss = currentData ? 
                    (trade.action === 'BUY' ? 
                      (currentData.last - trade.entryPrice) * trade.amount * trade.leverage :
                      (trade.entryPrice - currentData.last) * trade.amount * trade.leverage
                    ) : 0;
                  
                  return (
                    <div key={trade.id} className="position-card">
                      <div className="position-header">
                        <div className="position-info">
                          <span className="symbol">{trade.pair}</span>
                          <span className={`action ${trade.action.toLowerCase()}`}>{trade.action}</span>
                          <span className="volume">{trade.amount} lots</span>
                        </div>
                        <button 
                          className="close-position-btn"
                          onClick={() => closeTradFiPosition(trade.id)}
                        >
                          Close Position
                        </button>
                      </div>
                      <div className="position-details">
                        <div className="detail-row">
                          <span className="label">Entry Price:</span>
                          <span className="value">{trade.entryPrice.toFixed(5)}</span>
                        </div>
                        <div className="detail-row">
                          <span className="label">Current Price:</span>
                          <span className="value">{currentData?.last.toFixed(5) || 'N/A'}</span>
                        </div>
                        <div className="detail-row">
                          <span className="label">P&L:</span>
                          <span className={`value ${profitLoss >= 0 ? 'positive' : 'negative'}`}>
                            {profitLoss >= 0 ? '+' : ''}${profitLoss.toFixed(2)}
                          </span>
                        </div>
                        <div className="detail-row">
                          <span className="label">Leverage:</span>
                          <span className="value">1:{trade.leverage}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TradFiTrading;
